import React from 'react';
import { Text } from '@chakra-ui/react';
import { CopyBlock, GuideLayout, GuideTitle, OPERATORS_FB_GROUP, Section, Steps } from './GuideLayout';

export default function FacebookAdsGuidePage() {
  return (
    <GuideLayout
      title="Facebook and Instagram ads for wash and fold"
      description="How a gig worker creates one local Facebook and Instagram campaign for wash and fold pickup, with the photo, the text, the radius, and a daily budget."
      path="/slb/get-customers/facebook-ads"
    >
      <GuideTitle
        kicker="Paid. One campaign."
        heading="Facebook and Instagram, in the neighborhoods you drive."
        lede="On Meta, people are not searching for laundry. They are scrolling. The ad has to say the neighborhoods, the price, and that you pick up the bag. Interest checkboxes (“likes fitness,” “new parent”) are a weak aim. A tight map and a clear photo do the work."
      />

      <Section title="Create it in Ads Manager, not with Boost">
        <Text color="gray.700" mb={4}>
          Boost spends with almost no control over who sees it or when it stops. Ads Manager is at adsmanager.facebook.com. You need a Facebook Page in the business name, not your personal profile, and the same booking link you use everywhere else.
        </Text>
        <Steps items={[
          ['Page and account', 'The Page name matches the shop. Connect Instagram if you have it, so the same ad can run there. You do not need a new brand.'],
          ['Campaign', 'Objective: Traffic, or Sales if it lets you send people to your site. Special ad category: none. Laundry pickup is not housing, jobs, or credit. One campaign. Name it Wash and fold — CITY.'],
          ['Budget and schedule', '$10 to $15 a day. Run it seven days, including the evenings you can actually pick up. Turn it off on days you are at the other job.'],
          ['Audience', 'Living in a radius around the ZIPs in Service Area. Start with about 5 to 10 miles, small enough that you would drive it after work. Age 25 to 55 is a fair first cut for households that pay for wash and fold. Leave detailed interests empty on this first ad.'],
          ['Placements', 'Facebook and Instagram feeds. Skip Audience Network.'],
          ['The ad', 'One image: a stack of folded clothes, or a labeled bag at a door. Put the city and the price on the picture in large type. Primary text is below. The button is Learn more. The website is your booking page.'],
          ['Read the result as orders', 'After seven days, compare dollars spent with pickups booked from that link. A pile of likes is not a route. If nobody booked, change the photo and the first line once, then decide. Do not run three campaigns at once while you are still the driver.'],
        ]} />
      </Section>

      <CopyBlock label="Words on the picture">
{`Wash and fold pickup
CITY neighborhoods
$PRICE/lb · we pick up`}
      </CopyBlock>

      <CopyBlock label="Primary text">
{`I pick up laundry in NEIGHBORHOODS, wash and fold it, and bring it back in TURNAROUND.

$PRICE a pound. MINIMUM minimum. Book on your phone. You get a text when it is on the way back.`}
      </CopyBlock>

      <CopyBlock label="Headline">
{`Book a wash and fold pickup in CITY`}
      </CopyBlock>

      <Section title="Talk to other operators">
        <Text color="gray.700" mb={3}>
          Ads find customers. The operators group is the community: share successes, learn from each other, and help every wash and fold operator succeed — at home or at a laundromat. Request to join, then wait for approval.
        </Text>
        <Text as="a" href={OPERATORS_FB_GROUP} target="_blank" rel="noopener noreferrer" color="orange.600" fontWeight="700">
          Join Wash & Fold Operators
        </Text>
      </Section>

      <Section title="The second ad, only after the first one books">
        <Text color="gray.700">
          Keep the same campaign and add one more ad for a fuller bag: short-term rental sheets, or towels for a salon or gym. Those offers are written out on the campaign plans page. Point that ad at the same booking page, and use a commercial account when one building or one shop has several stops. Do not launch it in week one.
        </Text>
      </Section>
    </GuideLayout>
  );
}
